import { Alert, Badge, Button, Group, Stack, Switch, Table, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDateTime } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

export const DEPLETION_EXCEPTIONS_PATH = '/admin/v1/inventory/depletion-exceptions';

/** One thing that is being sold and is not coming off the shelf. */
interface NotMoving {
  id: string;
  variantId: string;
  itemNameEn: string;
  reason: string;
  componentNameEn?: string | null;
  detail?: string | null;
  occurrences: number;
  firstAt: string;
  lastAt: string;
  resolvedAt?: string | null;
}

/**
 * The dishes the bar is selling whose effect on the shelf could not be worked out, and what each
 * one needs.
 *
 * Selling a drink takes the rum out of the bottle now. When it cannot — nothing says what the dish
 * is made of, an ingredient was retired, a recipe asks for millilitres of something counted in
 * bottles with no conversion on file — the only other option is to post a zero, and a zero is a
 * silent lie: a shelf that never moves, a plate cost that reads as free, and a pour report with a
 * hole in it that nobody can see. There is no version of this where the owner is better off not
 * knowing, so it is a tab and not a log line.
 *
 * ONE ROW PER THING TO FIX. A missing bun on a burger sold forty times a night is one row with
 * "40 times" on it, not forty rows — the second shape is a list nobody opens twice. How long it has
 * been happening is on the row too, because a fault that started in March is a different
 * conversation from one that started last night.
 *
 * ONE OF THE FAULTS IS NOT ABOUT THE SHELF, and that is why this list is no longer called "sales
 * that are not moving stock". An ingredient nobody has ever bought in comes off the shelf at
 * exactly the right quantity and is recorded as having cost nothing, so the drink reads as pure
 * profit — the same absence the recipe screen one tab away says "at least" about. It belongs
 * beside these rather than on a list of its own: they are all one question, which is whether a
 * figure the owner is reading means what it looks like.
 *
 * "I have fixed this" takes it off the list and nothing else. It does not touch the recipe and it
 * does not delete the record; the next sale that fails the same way puts the row straight back,
 * which is the only honest test of whether the fix worked.
 */
export function SalesNotMovingStock() {
  const queryClient = useQueryClient();
  const [showDone, setShowDone] = useState(false);
  const path = showDone ? `${DEPLETION_EXCEPTIONS_PATH}?includeResolved=true` : DEPLETION_EXCEPTIONS_PATH;

  const rows = useQuery({
    queryKey: listKey(DEPLETION_EXCEPTIONS_PATH, showDone ? { includeResolved: 'true' } : undefined),
    queryFn: () => rawApi<NotMoving[]>('GET', path),
  });

  const clear = useMutation({
    mutationFn: (id: string) => rawApi('POST', `${DEPLETION_EXCEPTIONS_PATH}/${id}/resolve`, {}),
    onSuccess: () => {
      notifyDone('Taken off the list', 'It will come back on its own if it happens again.');
      void queryClient.invalidateQueries({ queryKey: listKey(DEPLETION_EXCEPTIONS_PATH) });
    },
    onError: (err: unknown) => notifyError(err, 'take that off the list'),
  });

  const list = rows.data ?? [];
  const open = list.filter((r) => !r.resolvedAt);

  /*
   * Nothing at all when there is nothing wrong, rather than a permanent empty table under a
   * permanent heading. The answer to "is anything not moving stock" is this block being here or
   * not — furniture that is empty most weeks is furniture that stops being read, which is the
   * reasoning `BillsThatDisagreeSection` arrived at for the same shape of list.
   */
  if (rows.isLoading || (open.length === 0 && !showDone)) return null;

  return (
    <Stack gap="md" mb="xl">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="sm" align="baseline">
            {/* NOT "sales that are not moving stock". The note at the top of this file says the
                list was renamed when the missing-price fault joined it, and the heading was never
                changed — so an ingredient that came off the shelf at exactly the right quantity sat
                under a sentence saying nothing came off the shelf for it. */}
            <Text fw={600}>Sales the shelf could not answer for</Text>
            {open.length > 0 && (
              <Badge color="orange" variant="light">
                {open.length}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed" maw={720}>
            These are being sold and something about what they take off the shelf could not be worked out —
            for most of them nothing came off at all, and for one of them the right amount came off and
            nothing says what it cost. Either way a figure you are reading somewhere means less than it looks
            like. Each one needs a recipe written, an ingredient put back on the stock list, the amount for
            one shelf unit filled in, or a delivery posted for something never bought in.
          </Text>
        </Stack>
        <Switch
          checked={showDone}
          onChange={(e) => setShowDone(e.currentTarget.checked)}
          label="Include ones already dealt with"
          size="sm"
        />
      </Group>

      {rows.isError && <Alert color="red">That list could not be loaded.</Alert>}

      {list.length === 0 && (
        <EmptyNote>
          Everything being sold comes off the shelf, at a price you have paid. Nothing here needs you.
        </EmptyNote>
      )}

      {list.length > 0 && (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>What is being sold</Table.Th>
                <Table.Th>What is wrong</Table.Th>
                <Table.Th>What to do</Table.Th>
                <Table.Th ta="right">Times</Table.Th>
                <Table.Th>Since</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((row) => (
                <Table.Tr key={row.id} opacity={row.resolvedAt ? 0.55 : 1}>
                  <Table.Td>
                    <Text fw={500}>{row.itemNameEn}</Text>
                    {row.componentNameEn && (
                      <Text size="xs" c="dimmed">
                        {row.componentNameEn}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={enumColor('depletionFault', row.reason) ?? 'orange'}
                      variant="light"
                      style={{ textTransform: 'none' }}
                    >
                      {enumLabel('depletionFault', row.reason)}
                    </Badge>
                  </Table.Td>
                  {/* The sentence the server wrote, which names the two units or the ingredient.
                      A reason on its own tells the reader there is a problem; this tells them
                      which field to go and fill in. */}
                  <Table.Td maw={340}>
                    <Text size="sm">{row.detail ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td ta="right">{row.occurrences}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDateTime(row.firstAt)}</Text>
                    {row.lastAt !== row.firstAt && (
                      <Text size="xs" c="dimmed">
                        last {formatDateTime(row.lastAt)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {row.resolvedAt ? (
                      <Text size="xs" c="dimmed">
                        Dealt with
                      </Text>
                    ) : (
                      <Button
                        size="compact-sm"
                        variant="light"
                        loading={clear.isPending && clear.variables === row.id}
                        onClick={() => clear.mutate(row.id)}
                      >
                        I have fixed this
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
